import React, {useEffect, useMemo} from 'react';
import Select, {components as selectComponents} from 'react-select';
import {makeStyles, useTheme} from '@material-ui/core/styles/index';
import useDomains from '../../../hooks/useDomains';

const faviconStyle = {
  width: 16,
  height: 16,
  marginRight: 8,
  borderRadius: 2,
  flexShrink: 0,
};

const useStyles = makeStyles(theme => ({
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 7,
  },
  controlAdornment: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
  },
}));

export function DomainSelect({selectedDomains, setSelectedDomains, insideControlAdornment = null}) {
  const classes = useStyles();
  const theme = useTheme();
  const {domains} = useDomains();

  // Select the domain if its only on in the list
  useEffect(() => {
    if (domains?.length === 1 && !selectedDomains) {
      setSelectedDomains([{value: domains[0].id, label: domains[0].title}]);
    }
  }, [domains]);

  const selectStyles = {
    container: provided => ({...provided, minWidth: '100%', marginTop: 0}),
    input: provided => ({...provided, color: 'inherit'}),
    indicatorsContainer: provided => ({
      ...provided,
      flexWrap: 'nowrap',
    }),
    indicatorSeparator: provided => ({
      ...provided,
      backgroundColor: theme.palette.graphColors.themeDarkGray,
    }),
    placeholder: provided => ({...provided, color: 'inherit', fontWeight: 'bold'}),
    singleValue: provided => ({
      ...provided,
      color: 'inherit',
      fontWeight: 'bold',
      fontSize: 14,
      opacity: 1,
    }),
    control: provided => ({
      ...provided,
      minHeight: 50,
      minWidth: '100%',
      marginTop: 0,
      background: 'inherit',
      border: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
      // This line disable the blue border
      boxShadow: '0 !important',
      '&:hover': {
        //  border: '0 !important',
      },
    }),
    multiValue: provided => ({
      ...provided,
      background: theme.palette.background.paper,
      // border: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
      borderRadius: 4,
      margin: 3,
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: 'inherit',
      fontWeight: 'bold',
      fontSize: 14,
    }),
    menu: provided => ({
      ...provided,
      background: theme.palette.background.default,
      boxShadow: '0 1px 20px rgba(0, 0, 0, .2)',
      zIndex: 9999999,
    }),
    menuList: provided => ({...provided, maxHeight: '60vh'}),
    option: (provided, state) => {
      return {
        ...provided,
        cursor: 'pointer',
        color: 'inherit',
        fontWeight: 'bold',
        fontSize: 14,
        opacity: state.isFocused ? 1 : 0.8,
        backgroundColor: state.isFocused ? 'rgba(199, 199, 199, .05)' : 'none',
        '&:active': {
          background: 'none',
          opacity: 1,
        },
        '&:hover': {
          opacity: 1,
        },
      };
    },
  };

  const mergedComponents = useMemo(() => {
    if (!insideControlAdornment) return undefined;
    return {
      IndicatorsContainer: props => (
        <selectComponents.IndicatorsContainer {...props}>
          <div
            role="presentation"
            className={classes.controlAdornment}
            onMouseDown={e => e.stopPropagation()}
          >
            {insideControlAdornment}
          </div>
          {props.children}
        </selectComponents.IndicatorsContainer>
      ),
    };
  }, [insideControlAdornment]);

  const formatOptionLabel = ({value, label}) => {
    const domain = label.replace(' domain', '');
    const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}`;
    return (
      <div className={classes.optionRow}>
        <img src={faviconUrl} alt="" style={faviconStyle} />
        <span style={{lineHeight: 1.4}}>{label}</span>
      </div>
    );
  };

  return (
    <div>
      <Select
        isMulti={false}
        isClearable
        placeholder="Select domain"
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
        components={mergedComponents}
        value={selectedDomains}
        onChange={setSelectedDomains}
        options={domains?.map(domain => {
          return {
            value: domain.id,
            label: domain.title,
          };
        })}
      />
    </div>
  );
}
